import React, { useEffect, useState } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import { convertIntToDecimalCoord } from 'helpers/formatter';

export { Weather };

function Weather() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);
  const [forecast, setForecast] = useState([]);
  const authUser = useSelector(x => x.auth.user);

  useEffect(() => {
    let cancelled = false;

    const apiKey = process.env.REACT_APP_WEATHER_API_KEY;
    if (!apiKey) {
      setError('Weather is not configured.');
      setLoading(false);
      return;
    }

    const fetchByCoords = (lat, lon) => {
      //default toronto
      if (lat === 0 || lon === 0 || lat === null || lon === null) {
        lat=43.6548;
        lon=-79.3884;
      }

      fetch(`https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&units=metric&appid=${apiKey}`)
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || 'Unable to load weather.');
          }
          return res.json();
        })
        .then((data) => {
          if (cancelled) return;
          if (!data || !data.list || !data.city) {
            throw new Error('Invalid weather response.');
          }

          if (data.city.country === 'CA') { data.city.country = 'Canada'; }

          setLocation({
            name: data.city.name,
            country: data.city.country,
          });

          const groups = {};
          data.list.forEach((item) => {
            const date = item.dt_txt.split(' ')[0];
            if (!groups[date]) {
              groups[date] = [];
            }
            groups[date].push(item);
          });

          const todayStr = new Date().toISOString().split('T')[0];
          const daily = Object.keys(groups)
            .sort()
            .filter((d) => d >= todayStr)
            .slice(0, 5)
            .map((date) => {
              const entries = groups[date];
              const midday =
                entries.find((e) => e.dt_txt.includes('12:00:00')) ||
                entries[Math.floor(entries.length / 2)];

              const tempsMin = entries.map((e) => e.main?.temp_min);
              const tempsMax = entries.map((e) => e.main?.temp_max);

              return {
                date,
                temp: Math.round(midday.main?.temp),
                min: Math.round(Math.min(...tempsMin)),
                max: Math.round(Math.max(...tempsMax)),
                description: midday.weather?.[0]?.description,
                icon: midday.weather?.[0]?.icon,
              };
            });

          setForecast(daily);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setError('Unable to load weather.');
          setLoading(false);
        });
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          fetchByCoords(latitude, longitude);
        },
        () => {
          // Fallback to a default city (Toronto, CA) if geolocation fails
          fetchByCoords(convertIntToDecimalCoord(parseInt(authUser?.lat)), convertIntToDecimalCoord(parseInt(authUser?.lng)));
        },
        { timeout: 8000 }
      );
    } else {
      // Geolocation not supported, use default
      fetchByCoords(convertIntToDecimalCoord(parseInt(authUser?.lat)), convertIntToDecimalCoord(parseInt(authUser?.lng)));
    }

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="weather-widget text-center">
        <div className="spinner-border spinner-border-sm"></div>
      </div>
    );
  }

  if (error || !location || forecast.length === 0) {
    return (
      <div className="weather-widget text-center">
        <span className="weather-error">{error || 'Weather unavailable.'}</span>
      </div>
    );
  }

  const formatDayLabel = (dateStr, index) => {
    const today = new Date();
    const d = new Date(dateStr + 'T00:00:00');
    const isToday =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();

    if (index === 0 && isToday) {
      return 'Today';
    }

    return d.toLocaleDateString(undefined, { weekday: 'short' });
  };

  return (
    <div className="weather-widget div-center">
      <div className="weather-header">
        <span className="weather-location">
          <Icon.GeoAltFill size={18} /> {location?.name}
          {location?.country && <span>, {location.country}</span>}
        </span>
      </div>
      <div className="weather-forecast">
        {forecast.map((day, index) => {
          const iconUrl = day.icon
            ? `https://openweathermap.org/img/wn/${day.icon}.png`
            : null;

          return (
            <div key={day.date} className="weather-day">
              <span className="weather-day-name">
                {formatDayLabel(day.date, index)}
              </span>
              {iconUrl && (
                <span
                  className="weather-day-icon"
                  style={{ backgroundImage: `url(${iconUrl})` }}
                />
              )}
              <span className="weather-day-temp">{day.temp}°C</span>
              <span className="weather-day-range">
                {day.min}° / {day.max}°
              </span>
              <span className="weather-day-desc">
                {day.description &&
                  day.description.charAt(0).toUpperCase() +
                    day.description.slice(1)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

